/**
 * Block Node Types
 *
 * BlockNode is a unified content node that combines:
 * - Rich text document editing (Tiptap/ProseMirror)
 * - Flow connectivity (receives data from upstream nodes)
 * - AI content generation (agents can stream content)
 *
 * This node type bridges the gap between structured workflow nodes
 * and free-form content creation, making it ideal for collaborative
 * human-AI document authoring.
 */

import { DEFAULT_BLOCK_FORMAT, type BlockFormatConfig } from './format';
import type { KnowledgeCitation } from './knowledge';

type FlowPosition = { x: number; y: number };

// ============================================================================
// CONNECTION TYPES
// ============================================================================

/**
 * Connection from an upstream node in the flow.
 *
 * BlockNode receives data from PortNode outputs, PlanNode results,
 * or other BlockNode outputs. These connections appear as clickable
 * "chips" that users can insert into the document.
 */
export interface BlockConnection {
  /** Unique ID of the connected node */
  nodeId: string;

  /** Display label for the connection (shown in chip) */
  nodeLabel: string;

  /** Type of the connected node (for styling/behavior) */
  nodeType: string;

  /** For image nodes: URL of the generated/uploaded image */
  imageUrl?: string;

  /** For text-producing nodes: the generated content */
  textContent?: string;
}

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * Data structure for BlockNode instances.
 *
 * This is stored in the React Flow node's data property and synced
 * via Yjs for real-time collaboration.
 */
export interface BlockNodeData {
  // ========== Identity ==========

  /** Block title (optional, displayed as chip above content) */
  title?: string;

  // ========== Content ==========

  /** Main content as HTML from Tiptap editor */
  content: string;

  // ========== Format ==========

  /** Layout format for published/rendered aspect ratio */
  format?: BlockFormatConfig;

  // ========== Flow Connections ==========

  /** Upstream nodes providing context (shown as chips) */
  incomingConnections?: BlockConnection[];

  /** Aggregated context content from all connections (for AI prompts) */
  contextContent?: string;

  // ========== AI Integration ==========

  /** If set, auto-generates content on mount using this as the AI prompt */
  initialPrompt?: string;

  // ========== Citations (from Knowledge Space answers) ==========

  /** Citations from Knowledge Space answers - displayed at bottom of block */
  citations?: KnowledgeCitation[];

  // ========== Collaboration (required for RichTextEditor) ==========

  /** Yjs document for real-time collaboration */
  ydoc?: any; // Y.Doc type (imported at runtime to avoid circular deps)

  /** Awareness for cursor/selection sync */
  awareness?: any; // Awareness type

  /** Board ID for inline comments */
  boardId?: string;

  // ========== Callbacks (for parent component state management) ==========

  /** Called when title changes */
  onTitleChange?: (title: string) => void;

  /** Called when content changes (user edit or AI generation) */
  onContentChange?: (content: string) => void;

  /** Called when incoming connections are reordered via drag-and-drop */
  onIncomingConnectionsChange?: (connections: BlockConnection[]) => void;

  // Index signature required for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for BlockNode.
 *
 * Use this type when working with block nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new block nodes
 * - In onNodesChange handlers
 */
export type BlockNodeType = {
  id: string;
  type: 'block';
  data: BlockNodeData;
  position: FlowPosition;
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default BlockNodeData with empty values.
 *
 * Use this when creating a new BlockNode to ensure all required
 * fields are initialized.
 */
export function createDefaultBlockData(): BlockNodeData {
  return {
    title: '',
    content: '<p></p>', // Empty Tiptap paragraph
    format: DEFAULT_BLOCK_FORMAT,
    incomingConnections: [],
    contextContent: '',
  };
}

/**
 * Check if BlockNode has any content (title or body text).
 *
 * Useful for determining if a block is "empty" for UI purposes
 * (showing placeholders, validation, etc.)
 */
export function hasBlockContent(data: BlockNodeData): boolean {
  const hasTitle = !!data.title && data.title.trim() !== '';
  const hasContent = !!data.content &&
    data.content !== '' &&
    data.content !== '<p></p>' &&
    data.content !== '<p></p>\n';

  return hasTitle || hasContent;
}
